import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import ScheduleSelector, { ScheduleConfig } from "../common/ScheduleSelector";
import { ContactListWithCount } from "../../types/contactList";
import { Workflow } from "../../types/workflow";
import { workflowService } from "../../services/workflowService";
import { ContactsService } from "../../services/contactsService";
import { FiCalendar, FiZap, FiFileText, FiBell, FiInfo } from "react-icons/fi";
import { UsersIcon } from "../common/icons";

// Type-safe icon wrappers
const CalendarIcon = FiCalendar as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;
const BellIcon = FiBell as React.FC<React.SVGProps<SVGSVGElement>>;
const InfoIcon = FiInfo as React.FC<React.SVGProps<SVGSVGElement>>;

// Import the sidebar.css which contains our brutalist design tokens
import "../../styles/sidebar.css";

interface WorkflowEditModalProps {
  workflow: Workflow;
  mode: "edit" | "preview";
  onClose: () => void;
  onUpdated: () => void;
  onDelete?: (workflowId: string) => Promise<void>;
}

const WorkflowEditModal: React.FC<WorkflowEditModalProps> = ({
  workflow,
  mode,
  onClose,
  onUpdated,
  onDelete,
}) => {
  const isPreview = mode === "preview";
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this is a VC-managed workflow
  const isVCManaged = workflow.is_vc_managed === true;
  const lockedFields = workflow.locked_fields || ["status", "schedule_enabled"];

  // Stabilize workflow reference to prevent infinite loops
  const [stableWorkflow] = useState<Workflow>(workflow);

  // Editable fields - initialize from stable workflow
  const [name, setName] = useState(stableWorkflow.name);
  const [description, setDescription] = useState(stableWorkflow.description || "");
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>(() => {
    const existing = (stableWorkflow.schedule_config || {}) as any;
    const tz = (stableWorkflow.timezone as string) || existing.timezone || "UTC";
    return {
      enabled: !!stableWorkflow.schedule_enabled,
      cron: existing.cron || "0 9 * * 1",
      timezone: tz,
      frequency: existing.frequency || "daily",
      days: existing.days || ["1"],
      time: existing.time || "09:00",
    } as ScheduleConfig;
  });
  const [contactListId, setContactListId] = useState<string | "">(
    stableWorkflow.contact_list_id || ""
  );
  const [notifications, setNotifications] = useState<Record<string, any>>(
    stableWorkflow.notifications || { on_success: true, on_failure: true }
  );

  const [contactLists, setContactLists] = useState<ContactListWithCount[]>([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let isInFlight = false;
    
    const loadLists = async () => {
      if (isInFlight) return; // Prevent concurrent loads using local flag
      
      isInFlight = true;
      try {
        setIsLoadingLists(true);
        const lists = await ContactsService.getContactLists();
        if (isMounted) {
          setContactLists(lists);
        }
      } catch (e) {
        // non-blocking
        console.warn("Failed to load contact lists", e);
      } finally {
        // Always reset loading state, regardless of mount status
        setIsLoadingLists(false);
        isInFlight = false;
      }
    };
    
    loadLists();
    
    return () => {
      isMounted = false;
    };
  }, []); // Only load once on mount

  // Get integrations display
  const integrations = useMemo(() => {
    if (stableWorkflow.selected_integrations && Array.isArray(stableWorkflow.selected_integrations)) {
      return stableWorkflow.selected_integrations.map((i: any) => {
        const name = typeof i === 'string' ? i : (i.provider || i.name || 'Unknown');
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      });
    }
    return [];
  }, [stableWorkflow.selected_integrations]);

  // Get insights display
  const insights = useMemo(() => {
    if (stableWorkflow.selected_insights && Array.isArray(stableWorkflow.selected_insights)) {
      return stableWorkflow.selected_insights.map((i: any) => 
        typeof i === 'string' ? i : (i.title || i.type || i.category || 'Unknown')
      );
    }
    return [];
  }, [stableWorkflow.selected_insights]);

  // Get contact list name
  const contactListName = useMemo(() => {
    const list = contactLists.find(l => l.id === stableWorkflow.contact_list_id);
    return list?.name || "Not selected";
  }, [contactLists, stableWorkflow.contact_list_id]);

  const handleSave = async () => {
    if (isPreview) return;
    setSaving(true);
    setError(null);
    try {
      const updates: Record<string, any> = {
        name,
        description,
        schedule_enabled: !!scheduleConfig?.enabled,
        schedule_config: scheduleConfig,
        timezone: scheduleConfig?.timezone || "UTC",
        contact_list_id: contactListId || null,
        notifications: notifications || {},
      };

      // Persist via hybrid workflow update endpoint
      await workflowService.updateWorkflow(stableWorkflow.id, updates);

      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || isVCManaged) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(stableWorkflow.id);
      onClose();
    } catch (e: any) {
      // Check for specific error messages
      const errorMsg = e?.message || "Failed to delete workflow";
      if (errorMsg.includes("VC-managed") || e?.reason === "vc_managed_workflow") {
        setError("Cannot delete a VC-managed workflow. This workflow is required for sending updates to your VC.");
      } else {
        setError(errorMsg);
      }
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // Section component using brutalist design tokens
  const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="su-brutalist-container-flat" style={{ backgroundColor: 'var(--su-brutalist-bg)' }}>
      <div 
        className="flex items-center gap-2 px-4 py-3"
        style={{ 
          borderBottom: '1px solid var(--su-brutalist-border)',
          backgroundColor: '#0a0a0a'
        }}
      >
        <span className="text-purple-400">{icon}</span>
        <h4 className="text-sm font-semibold text-white uppercase tracking-wide">{title}</h4>
      </div>
      <div className="p-4 space-y-4">
        {children}
      </div>
    </div>
  );

  // Input styling using CSS variables
  const inputClass = "w-full px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500";
  const inputStyle = {
    backgroundColor: '#0a0a0a',
    border: '1px solid var(--su-brutalist-border)',
    borderRadius: 'var(--su-brutalist-border-radius)',
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-80" />
      
      <div className="relative z-10 flex min-h-full items-start justify-center p-3 sm:p-4">
        {/* Modal Container - using brutalist container class */}
        <div 
          className="su-brutalist-container w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: 'var(--su-brutalist-bg)' }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4"
            style={{ borderBottom: '1px solid var(--su-brutalist-border)' }}
          >
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-white">
                {isPreview ? "View Workflow" : "Edit Workflow"}
              </h3>
              {isPreview && (
                <span 
                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400"
                  style={{
                    backgroundColor: '#0a0a0a',
                    border: '1px solid var(--su-brutalist-border)',
                  }}
                >
                  Read Only
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors text-xl"
            >
              ✕
            </button>
          </div>

          {error && (
            <div
              className="mx-4 mt-4 text-sm text-red-400 p-3"
              style={{
                backgroundColor: 'rgba(127, 29, 29, 0.3)',
                border: '1px solid #7f1d1d',
              }}
            >
              {error}
            </div>
          )}

          {/* VC-Managed Workflow Banner */}
          {isVCManaged && (
            <div
              className="mx-4 mt-4 text-sm p-3"
              style={{
                backgroundColor: 'rgba(147, 51, 234, 0.15)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
              }}
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-600 text-white">
                  VC Managed
                </span>
                <span className="text-purple-300">
                  This workflow sends automatic updates to your VC. Some settings cannot be changed.
                </span>
              </div>
            </div>
          )}

          <div className="p-4 space-y-4">
            {/* Basic Info Section */}
            <Section icon={<FileTextIcon className="w-4 h-4" />} title="Basic Information">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                  Name
                </label>
                {isPreview ? (
                  <p className="text-white text-sm py-2">{stableWorkflow.name}</p>
                ) : (
                  <input
                    className={inputClass}
                    style={inputStyle}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                  Description
                </label>
                {isPreview ? (
                  <p className="text-white text-sm py-2">{stableWorkflow.description || "—"}</p>
                ) : (
                  <textarea
                    className={inputClass}
                    style={inputStyle}
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                    Status
                  </label>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${
                      stableWorkflow.schedule_enabled 
                        ? "bg-[#14532d] text-[#4ade80]" 
                        : "bg-[#422006] text-[#facc15]"
                    }`}>
                      {stableWorkflow.schedule_enabled ? "ACTIVE" : "PAUSED"}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                    Workflow Type
                  </label>
                  <p className="text-white text-sm py-1">{stableWorkflow.workflow_type || "Custom"}</p>
                </div>
              </div>
            </Section>

            {/* Schedule Section */}
            <Section icon={<CalendarIcon className="w-4 h-4" />} title="Schedule">
              {/* Show warning if VC-managed and schedule is locked */}
              {isVCManaged && lockedFields.includes("schedule_enabled") && !isPreview && (
                <div
                  className="mb-4 text-xs p-2 text-purple-300"
                  style={{
                    backgroundColor: 'rgba(147, 51, 234, 0.1)',
                    border: '1px dashed rgba(147, 51, 234, 0.3)',
                  }}
                >
                  Schedule settings are managed by your VC and cannot be changed.
                </div>
              )}
              {isPreview || (isVCManaged && lockedFields.includes("schedule_enabled")) ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Frequency
                    </label>
                    <p className="text-white text-sm py-1">
                      {(stableWorkflow.schedule_config as any)?.frequency || "Not set"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Timezone
                    </label>
                    <p className="text-white text-sm py-1">{stableWorkflow.timezone || "UTC"}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Next Execution
                    </label>
                    <p className="text-white text-sm py-1">
                      {stableWorkflow.next_execution
                        ? new Date(stableWorkflow.next_execution).toLocaleString()
                        : "Not scheduled"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Last Executed
                    </label>
                    <p className="text-white text-sm py-1">
                      {stableWorkflow.last_executed_at
                        ? new Date(stableWorkflow.last_executed_at).toLocaleString()
                        : "Never"}
                    </p>
                  </div>
                </div>
              ) : (
                <ScheduleSelector
                  value={scheduleConfig}
                  onChange={(newVal) => setScheduleConfig(newVal)}
                />
              )}
            </Section>

            {/* Recipients Section */}
            <Section icon={<UsersIcon className="w-4 h-4" />} title="Recipients">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                  Contact List
                </label>
                {isPreview ? (
                  <p className="text-white text-sm py-1">{contactListName}</p>
                ) : (
                  <select
                    className={inputClass}
                    style={inputStyle}
                    value={contactListId}
                    onChange={(e) => setContactListId(e.target.value)}
                  >
                    <option value="">None</option>
                    {contactLists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}{" "}
                        {typeof l.contact_count === "number"
                          ? `(${l.contact_count} contacts)`
                          : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </Section>

            {/* Integrations Section */}
            <Section icon={<ZapIcon className="w-4 h-4" />} title="Integrations">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                  Connected Integrations
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {integrations.length > 0 ? (
                    integrations.map((integration, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1.5 text-white text-xs font-medium"
                        style={{
                          backgroundColor: '#1f2937',
                          border: '1px solid var(--su-brutalist-border)',
                        }}
                      >
                        {integration}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No integrations selected</p>
                  )}
                </div>
              </div>
            </Section>

            {/* Insights Section */}
            <Section icon={<InfoIcon className="w-4 h-4" />} title="Insights">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                  Selected Insights
                </label>
                <div className="mt-2 space-y-2">
                  {insights.length > 0 ? (
                    insights.map((insight, idx) => (
                      <div 
                        key={idx} 
                        className="px-3 py-2 text-white text-sm"
                        style={{
                          backgroundColor: '#0a0a0a',
                          border: '1px solid var(--su-brutalist-border)',
                        }}
                      >
                        {insight}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No insights selected</p>
                  )}
                </div>
              </div>
            </Section>

            {/* Notifications Section */}
            <Section icon={<BellIcon className="w-4 h-4" />} title="Notifications">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    id="notif-success"
                    type="checkbox"
                    className="text-purple-600 focus:ring-purple-500 bg-black"
                    style={{ 
                      border: '1px solid var(--su-brutalist-border)',
                      borderRadius: 0,
                    }}
                    checked={!!notifications?.on_success}
                    disabled={isPreview}
                    onChange={(e) =>
                      setNotifications({
                        ...(notifications || {}),
                        on_success: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="notif-success"
                    className={`text-sm ${isPreview ? "text-gray-500" : "text-gray-300"}`}
                  >
                    Notify on success
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="notif-failure"
                    type="checkbox"
                    className="text-purple-600 focus:ring-purple-500 bg-black"
                    style={{ 
                      border: '1px solid var(--su-brutalist-border)',
                      borderRadius: 0,
                    }}
                    checked={!!notifications?.on_failure}
                    disabled={isPreview}
                    onChange={(e) =>
                      setNotifications({
                        ...(notifications || {}),
                        on_failure: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="notif-failure"
                    className={`text-sm ${isPreview ? "text-gray-500" : "text-gray-300"}`}
                  >
                    Notify on failure
                  </label>
                </div>
              </div>
            </Section>

            {/* Stats Section (Preview only) */}
            {isPreview && (
              <Section icon={<InfoIcon className="w-4 h-4" />} title="Execution Stats">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Total Executions
                    </label>
                    <p className="text-white text-lg font-semibold">{stableWorkflow.execution_count || 0}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Successful
                    </label>
                    <p className="text-green-400 text-lg font-semibold">{stableWorkflow.success_count || 0}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                      Created
                    </label>
                    <p className="text-white text-sm">
                      {stableWorkflow.created_at 
                        ? new Date(stableWorkflow.created_at).toLocaleDateString() 
                        : "—"}
                    </p>
                  </div>
                </div>
              </Section>
            )}
          </div>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            >
              <div
                className="su-brutalist-container w-full max-w-md p-6"
                style={{ backgroundColor: 'var(--su-brutalist-bg)' }}
              >
                <h3 className="text-lg font-semibold text-white mb-2">Delete Workflow</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Are you sure you want to delete <span className="text-white font-medium">&quot;{stableWorkflow.name}&quot;</span>?
                  This action cannot be undone and will remove all execution history.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:bg-[#111] transition-colors"
                    style={{
                      border: '1px solid var(--su-brutalist-border)',
                      borderRadius: 'var(--su-brutalist-border-radius)',
                    }}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition-colors"
                    style={{ borderRadius: 'var(--su-brutalist-border-radius)' }}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete Workflow"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            className="p-4 flex items-center justify-between"
            style={{ borderTop: '1px solid var(--su-brutalist-border)' }}
          >
            {/* Delete button - only show in edit mode and if onDelete is provided */}
            <div>
              {!isPreview && onDelete && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: 'var(--su-brutalist-border-radius)',
                  }}
                  disabled={saving || isVCManaged}
                  title={isVCManaged ? "Cannot delete VC-managed workflows" : "Delete this workflow"}
                >
                  {isVCManaged ? "Cannot Delete (VC Managed)" : "Delete Workflow"}
                </button>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:bg-[#111] transition-colors"
                style={{
                  border: '1px solid var(--su-brutalist-border)',
                  borderRadius: 'var(--su-brutalist-border-radius)',
                }}
              >
                {isPreview ? "Close" : "Cancel"}
              </button>
              {!isPreview && (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-60 transition-colors"
                  style={{ borderRadius: 'var(--su-brutalist-border-radius)' }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkflowEditModal;
